import { useState, type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import clsx from 'clsx';

interface SortableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (next: T[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderItem: (item: T, handleProps: { listeners: any; attributes: any }) => ReactNode;
  /** Optional: a className applied to the outer container. */
  className?: string;
}

/**
 * Tiny drag-and-drop list primitive.
 * The drag handle is rendered as part of the row; rows themselves are not
 * drag-affordances (so clickable children like buttons keep working).
 */
export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  renderItem,
  className,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className={clsx('flex flex-col gap-3', className)}>
          {items.map((item) => (
            <SortableRow key={item.id} id={item.id}>
              {(handleProps) => renderItem(item, handleProps)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableRowProps {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (handleProps: { listeners: any; attributes: any }) => ReactNode;
}

function SortableRow({ id, children }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={clsx(isDragging && 'shadow-fg-3 ring-2 ring-red-500/40 rounded-lg')}>
      {children({ listeners, attributes })}
    </div>
  );
}

/**
 * A small drag handle the row can put anywhere.
 * Wrapped in a button (so it's keyboard-focusable) that captures pointer events
 * — the row itself stays non-draggable.
 */
export function DragHandle(props: {
  // dnd-kit's SyntheticListenerMap / DraggableAttributes don't expose index
  // signatures, so we accept them as structural `any` here and re-cast on spread.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listeners: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: any;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label="Drag to reorder"
      {...(props.attributes as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      {...(props.listeners as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      className={clsx(
        'cursor-grab rounded p-2 text-[#8E8E93] hover:bg-white/10 hover:text-[#FAFAFA] active:cursor-grabbing fg-anim-fast touch-none',
        props.className,
      )}
    >
      <GripVertical size={16} />
    </button>
  );
}

/**
 * Tiny inline component used by pages that already hold an `items` array
 * but want controlled optimistic state during drag.
 */
export function useOptimisticOrder<T extends { id: string }>(initial: T[]) {
  return useState<T[]>(initial);
}
